// eslint-disable-next-line no-restricted-imports
import store from '@/vuex/store';

// source categories pull from src/pendo.io/types/vocsource.go
export const SOURCE_CATEGORIES = {
    FeedbackV1: {
        id: 'FeedbackV1',
    },
    Integrations: {
        id: 'Integrations',
    },
    BulkImport: {
        id: 'BulkImport',
    },
    PendoUI: {
        id: 'PendoUI',
    },
    PendoV1Api: {
        id: 'PendoV1Api',
    },
    Surveys: {
        id: 'Surveys',
    },
    VisitorExperience: {
        id: 'VisitorExperience',
    },
};

export const SOURCE_DISPLAY_CATEGORIES = {
    Listen: {
        id: 'Listen',
        displayOrder: 1,
    },
    Portals: {
        id: 'Idea portals',
        displayOrder: 2,
    },
    Guides: {
        id: 'Guides',
        displayOrder: 3,
    },
    NPS: {
        id: 'NPS',
        displayOrder: 4,
    },
    Integrations: {
        id: 'Integrations',
        displayOrder: 5,
    },
    Imports: {
        id: 'Imports',
        displayOrder: 6,
    },
    Surveys: {
        id: 'Surveys',
        displayOrder: 7,
    },
};

export const ALL_PORTALS_NAME = 'All idea portals';

/**
 * @typedef {Object} SourceConfig
 * @property {string} id - Identifier of the source.
 * @property {string} label - Label to be used in the Feedback table/filter.
 * @property {Object} sourceCategory - Backend-defined category of the source.
 * @property {Object} sourceDisplayCategory - Category of the source for feedback table filter.
 * @property {number} displayOrder - Order of the source to be displayed in feedback table filter.
 * @property {string} icon - Icon for the source.
 * @property {string} iconWithName - Icon for the source with name.
 * @property {string} [sourceType] - Type of the source for the type filter in connect sources.
 * @property {number} [periodicity] - How frequently the integration sync should be run.
 * @property {Object} [configValuesMap] - Map of config values to be used in the create source config binding.
 * @property {(objectId: string) => string} [connectSourcesTitle] - Title to be used in the connect sources table. If not provided, the label will be used.
 * @property {(objectId: string) => string} [deleteConfigIdentifier] - Identifier to be used to delete the source config.
 * @property {(objectId: string) => string} [sourceConfigToVocItemSourceAggFilter] - Formatter for the connect sources table, mapping the config object ID to the source identifier of the VOC item.
 * @property {boolean} [hasSettings] - Boolean to indicate if the source has settings, making it a clickable link in the connect sources table.
 * @property {(source: any) => Record<string, any>} [getFormattedLabel] - function to create a formatted label for the source, if not provided, the static label will be used
 * @property {boolean} [hasReplay] - Boolean to indicate if the source has replay, making it a clickable link in the connect sources table.
 * @property {boolean} [hasCall] - Boolean to indicate if the source supports playing back call recordings in the call drawer.
 * @property {boolean} [hasJumpToCall] - Boolean to indicate if the source supports deep linking to sections of a call recording by timestamp.
 * @property {string} [component] - Component to be used to display the source.
 * @property {string} [ticketTypeLabel] - Label to be used to display the source.
 * @property {boolean} [availableForPortal] - Boolean to indicate if the source is available for feedback in the portals.
 */

/**
 * Configuration object for various data sources.
 * @type {Object.<string, SourceConfig>}
 * @constant
 * @exports SOURCES
 * @description Configuration object for various data sources.
 */

export const ALL_SOURCES = {
    CSV: {
        id: 'CSV',
        label: 'CSV',
        sourceCategory: SOURCE_CATEGORIES.BulkImport,
        sourceDisplayCategory: SOURCE_DISPLAY_CATEGORIES.Imports,
        displayOrder: 2,
        icon: 'source-icon/CSV-icon.svg',
        iconWithName: 'source-icon/CSV-icon-withname.svg',
        sourceType: 'CSV',
        connectSourcesTitle: (objectId) => `'${objectId}' file`,
        deleteConfigIdentifier: (objectId) => `${objectId}  `,
        sourceConfigToVocItemSourceAggFilter: (objectId) => {
            return `(startsWith(sourceIdentifier, "${objectId}  "))`;
        },
        getFormattedLabel: (source) => source.sourceIdentifier || 'Unknown source',
    },
    Feedback: {
        id: 'Feedback',
        label: 'Feedback',
        icon: 'source-icon/source-pendo-icon.svg',
        iconWithName: 'source-icon/source-pendo-icon-withname.svg',
        sourceCategory: SOURCE_CATEGORIES.PendoUI,
        sourceDisplayCategory: SOURCE_DISPLAY_CATEGORIES.Listen,
        displayOrder: 7,
    },
    FeedbackV1: {
        id: 'FeedbackV1',
        label: 'Feedback V1',
        icon: 'source-icon/feedbackV1-icon.svg',
        iconWithName: 'source-icon/feedbackV1-icon-withname.svg',
        sourceCategory: SOURCE_CATEGORIES.FeedbackV1,
        sourceDisplayCategory: SOURCE_DISPLAY_CATEGORIES.Imports,
        displayOrder: 1,
    },
    IdeaDetail: {
        id: 'IdeaDetail',
        label: 'Idea Detail',
        icon: 'source-icon/source-pendo-icon.svg',
        iconWithName: 'source-icon/source-pendo-icon-withname.svg',
        sourceCategory: SOURCE_CATEGORIES.PendoUI,
        sourceDisplayCategory: SOURCE_DISPLAY_CATEGORIES.Listen,
        displayOrder: 4,
    },
    IdeaPoll: {
        id: 'IdeaPoll',
        label: 'Idea Poll',
        icon: 'source-icon/source-pendo-icon.svg',
        iconWithName: 'source-icon/source-pendo-icon-withname.svg',
        sourceCategory: SOURCE_CATEGORIES.PendoUI,
        sourceDisplayCategory: SOURCE_DISPLAY_CATEGORIES.Listen,
        displayOrder: 5,
    },
    IdeaList: {
        id: 'IdeaList',
        label: 'Idea List',
        icon: 'source-icon/source-pendo-icon.svg',
        iconWithName: 'source-icon/source-pendo-icon-withname.svg',
        sourceCategory: SOURCE_CATEGORIES.PendoUI,
        sourceDisplayCategory: SOURCE_DISPLAY_CATEGORIES.Listen,
        displayOrder: 6,
    },
    InternalFeedbackForm: {
        id: 'InternalFeedbackForm',
        label: 'Internal Form',
        sourceCategory: SOURCE_CATEGORIES.PendoUI,
        sourceDisplayCategory: SOURCE_DISPLAY_CATEGORIES.Listen,
        sourceType: 'Internal feedback form',
        displayOrder: 1,
        icon: 'source-icon/internalForm-icon.svg',
        iconWithName: 'source-icon/internalForm-icon-withname.svg',
        connectSourcesTitle: () => 'Internal feedback form',
        deleteConfigIdentifier: () => '',
        sourceConfigToVocItemSourceAggFilter: () => {
            return '(sourceCategory == "PendoUI" && source == "InternalFeedbackForm")';
        },
    },
    NPS: {
        id: 'NPS',
        label: 'NPS Survey',
        sourceCategory: SOURCE_CATEGORIES.PendoUI,
        sourceDisplayCategory: SOURCE_DISPLAY_CATEGORIES.NPS,
        displayOrder: 1,
        deleteConfigIdentifier: (objectId) => objectId,
        sourceType: 'NPS',
        icon: 'source-icon/NPS-icon.svg',
        iconWithName: 'source-icon/NPS-icon-withname.svg',
        hasSettings: true,
        getFormattedLabel: (source) => source.guideName || 'NPS Survey',
        hasReplay: true,
        component: 'NpsResponse',
        ticketTypeLabel: 'NPS response',
        availableForPortal: true,
    },
    PendoV1Api: {
        id: 'PendoV1Api',
        label: 'V1 API',
        icon: 'source-icon/API-icon.svg',
        iconWithName: 'source-icon/API-icon-withname.svg',
        sourceCategory: SOURCE_CATEGORIES.PendoV1Api,
        sourceDisplayCategory: SOURCE_DISPLAY_CATEGORIES.Listen,
        displayOrder: 3,
    },
    PollResponse: {
        id: 'PollResponse',
        sourceCategory: SOURCE_CATEGORIES.PendoUI,
        sourceDisplayCategory: SOURCE_DISPLAY_CATEGORIES.Guides,
        displayOrder: 1,
        label: 'Guide',
        sourceType: 'Guides',
        icon: 'source-icon/guide-icon.svg',
        iconWithName: 'source-icon/guide-icon-withname.svg',
        deleteConfigIdentifier: (objectId) => objectId,
        sourceConfigToVocItemSourceAggFilter: (objectId) => {
            return `(startsWith(sourceIdentifier, "${objectId}__"))`;
        },
        hasSettings: true,
        getFormattedLabel: (source) => source.guideName || 'Guide',
        hasReplay: true,
        availableForPortal: true,
    },
    Portal: {
        id: 'Portal',
        label: 'Portal',
        sourceType: 'Portals',
        icon: 'source-icon/portal-icon.svg',
        iconWithName: 'source-icon/portal-icon-withname.svg',
        sourceCategory: SOURCE_CATEGORIES.VisitorExperience,
        sourceDisplayCategory: SOURCE_DISPLAY_CATEGORIES.Portals,
        getFormattedLabel: (source) => {
            if (source.sourceIdentifier === '') return ALL_PORTALS_NAME;

            const portal = store.getters['vocPortal/getPortalById'](source.sourceIdentifier);
            if (portal) return portal.internalName;

            return '(Deleted idea portal)';
        },
        sourceConfigToVocItemSourceAggFilter: (objectId) => {
            return `(source == "Portal" && sourceIdentifier == "${objectId}")`;
        },
        displayOrder: 2,
        availableForPortal: true,
        hasSettings: true,
    },
    Surveys: {
        id: 'Surveys',
        label: 'Surveys',
        sourceType: 'Surveys',
        icon: 'source-icon/surveys-icon.svg',
        iconWithName: 'source-icon/surveys-icon-withname.svg',
        sourceCategory: SOURCE_CATEGORIES.Surveys,
        sourceDisplayCategory: SOURCE_DISPLAY_CATEGORIES.Surveys,
        displayOrder: 1,
    },
    Salesforce: {
        id: 'Salesforce',
        label: 'Salesforce',
        icon: 'source-icon/Salesforce-icon.svg',
        iconWithName: 'source-icon/Salesforce-icon-withname.svg',
        sourceCategory: SOURCE_CATEGORIES.Integrations,
        sourceDisplayCategory: SOURCE_DISPLAY_CATEGORIES.Integrations,
        displayOrder: 1,
        sourceType: 'Salesforce',
        deleteConfigIdentifier: () => '',
        sourceConfigToVocItemSourceAggFilter: () => {
            return '(sourceCategory == "Integrations" && source == "Salesforce")';
        },
        hasSettings: true,
    },
    ZendeskTickets: {
        id: 'ZendeskTickets',
        label: 'Zendesk',
        sourceCategory: SOURCE_CATEGORIES.Integrations,
        sourceDisplayCategory: SOURCE_DISPLAY_CATEGORIES.Integrations,
        displayOrder: 2,
        sourceType: 'Zendesk',
        icon: 'source-icon/ZendeskTickets-icon.svg',
        iconWithName: 'source-icon/ZendeskTickets-icon-withname.svg',
        connectSourcesTitle: () => 'Zendesk tickets',
        deleteConfigIdentifier: () => '',
        sourceConfigToVocItemSourceAggFilter: () => {
            return '(sourceCategory == "Integrations" && source == "ZendeskTickets")';
        },
        component: 'SupportTicket',
        ticketTypeLabel: 'Support ticket',
    },
    Gong: {
        id: 'Gong',
        label: 'Gong',
        icon: 'source-icon/Gong-icon.svg',
        iconWithName: 'source-icon/Gong-icon-withname.svg',
        sourceDisplayCategory: SOURCE_DISPLAY_CATEGORIES.Integrations,
        displayOrder: 3,
        sourceType: 'Gong',
        dataType: 'call transcripts',
        dataInputType: 'call',
        periodicity: 24,
        configValuesMap: {
            accVisLookupMethod: 'salesforce',
        },
        connectSourcesTitle: () => 'Gong call transcripts',
        deleteConfigIdentifier: () => '',
        sourceConfigToVocItemSourceAggFilter: (objectId) => {
            return `(sourceCategory == "Gong" && source == "${objectId}")`;
        },
        hasCall: true,
        hasJumpToCall: true,
        component: 'CallTranscript',
        ticketTypeLabel: 'Call transcript',
    },
    ZoomTranscripts: {
        id: 'ZoomTranscripts',
        label: 'Zoom',
        icon: 'source-icon/Zoom-icon.svg',
        iconWithName: 'source-icon/Zoom-icon-withname.svg',
        sourceDisplayCategory: SOURCE_DISPLAY_CATEGORIES.Integrations,
        displayOrder: 4,
        sourceType: 'Zoom',
        dataType: 'call transcripts',
        dataInputType: 'call',
        periodicity: 24,
        configValuesMap: {
            accVisLookupMethod: 'pendo',
        },
        connectSourcesTitle: () => 'Zoom meeting transcripts',
        deleteConfigIdentifier: () => '',
        sourceConfigToVocItemSourceAggFilter: (objectId) => {
            return `(sourceCategory == "ZoomTranscripts" && source == "${objectId}")`;
        },
        hasCall: true,
        hasJumpToCall: false,
        component: 'CallTranscript',
        ticketTypeLabel: 'Meeting transcript',
    },
    Teams: {
        id: 'Teams',
        label: 'Teams',
        icon: 'source-icon/Teams-icon.svg',
        iconWithName: 'source-icon/Teams-icon-withname.svg',
        sourceDisplayCategory: SOURCE_DISPLAY_CATEGORIES.Integrations,
        displayOrder: 5,
        sourceType: 'Teams',
        dataType: 'call transcripts',
        dataInputType: 'call',
        periodicity: 24,
        configValuesMap: {
            accVisLookupMethod: 'pendo',
        },
        connectSourcesTitle: () => 'Teams meeting transcripts',
        deleteConfigIdentifier: () => '',
        sourceConfigToVocItemSourceAggFilter: (objectId) => {
            return `(sourceCategory == "Teams" && source == "${objectId}")`;
        },
        hasCall: true,
        hasJumpToCall: false,
        component: 'CallTranscript',
        ticketTypeLabel: 'Meeting transcript',
    },
    SlackMessages: {
        id: 'SlackMessages',
        label: 'Slack messages',
        icon: 'source-icon/SlackMessages-icon.svg',
        iconWithName: 'source-icon/SlackMessages-icon-withname.svg',
        sourceDisplayCategory: SOURCE_DISPLAY_CATEGORIES.Integrations,
        displayOrder: 6,
        sourceType: 'Slack',
        dataType: 'messages',
        dataInputType: 'message',
        configValuesMap: {
            accVisLookupMethod: 'pendo',
            internal_account_join_key: 'auto::id',
            internal_visitor_join_key: 'auto::id',
        },
        connectSourcesTitle: () => 'Slack messages',
        deleteConfigIdentifier: () => '',
        sourceConfigToVocItemSourceAggFilter: (objectId) => {
            return `(sourceCategory == "SlackMessages" && source == "${objectId}")`;
        },
        hasCall: false,
        hasJumpToCall: false,
        component: 'ChatTranscript',
        ticketTypeLabel: 'Slack message',
    },
};

export const SOURCES = {
    ...ALL_SOURCES,
    FeedbackForm: ALL_SOURCES.InternalFeedbackForm,
    Guide: ALL_SOURCES.PollResponse,
    PRODUCT_MARKET_FIT: ALL_SOURCES.Surveys,
    VocSourceFeedbackV1: ALL_SOURCES.FeedbackV1,
};

export const FEEDBACK_TYPE_CATEGORIES = {
    frustration: {
        label: 'Frustration',
        color: 'error',
    },
    compliments: {
        label: 'Compliments',
        color: 'success',
    },
    competition: {
        label: 'Competition',
        color: 'warning',
    },
};

export const FEEDBACK_TYPES = {
    'product enhancement request': {
        id: 'request',
        label: 'Request',
        icon: 'message-circle',
        category: 'frustration',
        filterValue: 'Product Enhancement Request',
        borderColor: '--palette-blue-50',
        bgColor: '--raw-palette-blue-50',
    },
    'product issues': {
        id: 'bug',
        label: 'Bug',
        icon: 'bug',
        category: 'frustration',
        filterValue: 'Product Issues',
        borderColor: '--palette-red-30',
        bgColor: '--raw-palette-red-30',
    },
    'pain point': {
        id: 'pain-point',
        label: 'Pain point',
        icon: 'angry',
        category: 'frustration',
        filterValue: 'Pain Point',
        borderColor: '--palette-orange-60',
        bgColor: '--raw-palette-orange-60',
    },
    'positive product feedback': {
        id: 'positive-product-feedback',
        label: 'Compliment',
        icon: 'heart',
        category: 'compliments',
        filterValue: 'Positive Product Feedback',
        borderColor: '--palette-green-50',
        bgColor: '--raw-palette-green-50',
    },
    'competitor strength': {
        id: 'competitor-strength',
        label: 'Competitor strength',
        icon: 'thumbs-down',
        category: 'competition',
        filterValue: 'Competitor Strength',
        borderColor: '--palette-purple-80',
        bgColor: '--raw-palette-purple-80',
    },
    'competitor weakness': {
        id: 'competitor-weakness',
        label: 'Competitor weakness',
        icon: 'thumbs-up',
        category: 'competition',
        filterValue: 'Competitor Weakness',
        borderColor: '--palette-yellow-60',
        bgColor: '--raw-palette-yellow-60',
    },
};

export const SPEAKER_TYPES = {
    CUSTOMER: 'customer speaker',
    INTERNAL: 'internal speaker',
};

export const SUPPORT_ARTICLES = {
    MIGRATED_FUNCTIONALITY_IN_LISTEN: 'https://support.pendo.io/hc/en-us/articles/29283526814747',
};
